import type { DataProvider } from "../../types/index";


interface SheetsConfig {
  endpoint: string;
  spreadsheetId: string;
  sheets: Record<string, string>;
  apiKey?: string;
}

interface QueueItem {
  action: string;
  payload: any;
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
}

export class GoogleSheetsProvider implements DataProvider {
  private config: SheetsConfig;
  private requestQueue: QueueItem[] = [];
  private isProcessing = false;
  private retryDelayMs = 2000;
  private maxRetries = 3;

  constructor(config: SheetsConfig) {
    this.config = config;
  }

  get name(): string {
    return "Google Sheets";
  }

  private async throttledRequest(action: string, payload: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.requestQueue.push({ action, payload, resolve, reject });
      this.processQueue();
    });
  }

  private async processQueue(): Promise<void> {
    if (this.isProcessing || this.requestQueue.length === 0) return;
    this.isProcessing = true;

    while (this.requestQueue.length > 0) {
      const { action, payload, resolve, reject } = this.requestQueue.shift()!;
      try {
        const result = await this.requestWithRetry(action, payload);
        resolve(result);
      } catch (err) {
        reject(err);
      }
      await new Promise((r) => setTimeout(r, 300));
    }

    this.isProcessing = false;
  }

  private async requestWithRetry(action: string, payload: any, attempt = 1): Promise<any> {
    try {
      return await this.makeRequest(action, payload);
    } catch (err: any) {
      const isQuotaError = err.message.includes("Quota exceeded");
      const shouldRetry = isQuotaError && attempt < this.maxRetries;

      if (shouldRetry) {
        const delayMs = this.retryDelayMs * Math.pow(2, attempt - 1);
        console.warn(`[Sheets] Quota hit, retrying in ${delayMs}ms (attempt ${attempt}/${this.maxRetries})`);
        await new Promise((r) => setTimeout(r, delayMs));
        return this.requestWithRetry(action, payload, attempt + 1);
      }

      throw err;
    }
  }

  async listAssets(): Promise<any[]> {
    try {
      const rows = await this.readSheet(this.sheetName("assets"));
      const mapped = rows.map((row: any, index: number) => this.mapAssetRow(row, index)).filter(Boolean);
      // EF-DATA-3: dedupe by (globalEAN + hub) — sheet stores one row per language/locale
      // which inflates counts. Keep first occurrence; merge nothing (data is identical).
      const seen = new Set<string>();
      const out: any[] = [];
      for (const a of mapped) {
        const ean = String(a.globalEAN || a.id || "").trim();
        const hub = String(a.hub || "").trim();
        const key = `${ean}::${hub}`;
        if (ean && seen.has(key)) continue;
        if (ean) seen.add(key);
        out.push(a);
      }
      return out;
    } catch (err: any) {
      console.error("[Sheets] listAssets failed:", err.message);
      return [];
    }
  }

  async listForecasts(): Promise<any[]> {
    try {
      const rows = await this.readSheet(this.sheetName("forecasts"));
      const mapped = rows.map((row: any, index: number) => this.mapForecastRow(row, index)).filter(Boolean);
      // EF-DATA-1: enrich child-only forecast rows by joining Assets on EAN
      // (project name / type / OCD are missing for many child rows in the sheet).
      try {
        const assets = await this.listAssets();
        const byEan = new Map<string, any>();
        for (const a of assets) {
          const ean = String(a.globalEAN || "").trim();
          if (ean && !byEan.has(ean)) byEan.set(ean, a);
        }
        for (const f of mapped) {
          const ean = String(f.assetId || "").trim();
          if (!ean) continue;
          const a = byEan.get(ean);
          if (!a) continue;
          if (!f.projectName) f.projectName = a.projectName || "";
          if (!f.brand) f.brand = a.brand || "";
          if (!f.division) f.division = a.division || "";
          if (!f.type) f.type = a.type || "";
          if (!f.ocdDate) f.ocdDate = a.dmiMadDate || "";
        }
      } catch {
        // best effort enrichment — don't break listForecasts if Assets fails
      }
      return mapped;
    } catch (err: any) {
      console.warn("[Sheets] listForecasts failed (sheet may not exist yet):", err.message);
      return [];
    }
  }

  async createMaster(payload: any): Promise<any> {
    const sheetName = this.sheetName("assets");

    const headerResult = await this.request("read", {
      sheetName,
      range: `${sheetName}!1:1`
    });
    const headerValues = headerResult?.data?.values || headerResult?.values || [];
    const headers = headerValues[0] || [];

    const fieldMap: Record<string, string> = {
      "QUARTER": payload.quarter || "",
      "DIVISION": payload.division || "",
      "BRAND": payload.brand || "",
      "TYPE": payload.type || "",
      "Project name": payload.projectName || "",
      "Product name": payload.productName || "",
      "PARENT/CHILD": "Parent",
      "DMI MAD DATE": "",
      "Initiative origine (Zone / local)": payload.initiativeOrigin || "",
      "GLOBAL EAN": payload.globalEAN || "",
      "HUB": "",
      "Launch confirmation": "",
      "Requested delivery date": payload.requestedDeliveryDate || ""
    };

    const rowValues = headers.length
      ? headers.map((h: string) => fieldMap[h] ?? "")
      : Object.values(fieldMap);

    await this.request("append", { sheetName, values: [rowValues] });

    const id = this.buildAssetId(
      { brand: payload.brand || "", product_name: payload.productName || "", global_ean: payload.globalEAN || "", hub: "" },
      Date.now()
    );

    const { nbAsset, nbLanguages, translationPct } = await this.resolveVolumeInputs({
      ...payload,
      parentChild: "Parent"
    });
    const assetVolume = Math.round(nbAsset * nbLanguages * translationPct);

    return {
      id,
      createdAt: new Date().toISOString(),
      createdBy: "user",
      assetVolume,
      volumeBreakdown: { nbAsset, nbLanguages, translationPct },
      notified: { sent: 0, recipients: [] }
    };
  }

  async createChildAsset(payload: any): Promise<any> {
    const sheetName = this.sheetName("assets");

    const headerResult = await this.request("read", {
      sheetName,
      range: `${sheetName}!1:1`
    });
    const headerValues = headerResult?.data?.values || headerResult?.values || [];
    const headers = headerValues[0] || [];

    const fieldMap: Record<string, string> = {
      "QUARTER": payload.quarter || "",
      "DIVISION": payload.division || "",
      "BRAND": payload.brand || "",
      "TYPE": payload.type || "",
      "Project name": payload.projectName || "",
      "Product name": payload.productName || "",
      "PARENT/CHILD": "Child",
      "DMI MAD DATE": payload.dmiMadDate || "",
      "Initiative origine (Zone / local)": payload.initiativeOrigin || "",
      "GLOBAL EAN": payload.globalEAN || "",
      "HUB": payload.hub || "",
      "Launch confirmation": payload.launchConfirmation || "",
      "Requested delivery date": payload.requestedDeliveryDate || ""
    };

    const rowValues = headers.length
      ? headers.map((h: string) => fieldMap[h] ?? "")
      : Object.values(fieldMap);

    await this.request("append", { sheetName, values: [rowValues] });

    const id = this.buildAssetId(
      {
        brand: payload.brand || "",
        product_name: payload.productName || "",
        global_ean: payload.globalEAN || "",
        hub: payload.hub || ""
      },
      Date.now()
    );

    const { nbAsset, nbLanguages, translationPct } = await this.resolveVolumeInputs({
      ...payload,
      parentChild: "Child"
    });
    const assetVolume = Math.round(nbAsset * nbLanguages * translationPct);

    return {
      id,
      assetVolume,
      createdAt: new Date().toISOString(),
      launchConfirmation: payload.launchConfirmation || ""
    };
  }

  async listRules(): Promise<any[]> {
    try {
      const rows = await this.readSheet(this.sheetName("rules"));
      return rows.map((row: any, index: number) => this.mapRuleRow(row, index)).filter(Boolean);
    } catch (err: any) {
      console.warn("[Sheets] listRules failed (sheet may not exist yet):", err.message);
      return [];
    }
  }

  async upsertRule(payload: any): Promise<any> {
    const headers = [
      "id", "hub", "division", "nb_of_asset_parent", "nb_of_asset_child",
      "nb_of_languages", "language_list", "%_asset_translated"
    ];
    const sheetName = this.sheetName("rules");

    await this.ensureHeaders(sheetName, headers);

    const serializeValue = (val: any) => String(val ?? "");

    if (payload.id) {
      const rowIndex = await this.findRowIndex(sheetName, "id", payload.id);
      if (rowIndex !== -1) {
        const sheetRow = rowIndex + 2;
        const lastCol = String.fromCharCode(65 + headers.length - 1);
        const rowValues = headers.map((h) => serializeValue(payload[h]));
        await this.request("update", {
          sheetName,
          range: `A${sheetRow}:${lastCol}${sheetRow}`,
          values: [rowValues]
        });
        return { ...payload };
      }
    }

    const id = payload.id || crypto.randomUUID();
    const rowValues = headers.map((h) => serializeValue(h === "id" ? id : payload[h]));
    await this.request("append", { sheetName, values: [rowValues] });
    return { id, ...payload };
  }

  async deleteRule(id: string): Promise<void> {
    const sheetName = this.sheetName("rules");
    const rowIndex = await this.findRowIndex(sheetName, "id", id);
    if (rowIndex === -1) return;

    const sheetRow = rowIndex + 2;
    await this.request("clear", {
      sheetName,
      range: `A${sheetRow}:Z${sheetRow}`
    });
  }

  // ── audit C7 / T9 — Data Access Rules ─────────────────────
  // Stored in the Data_Access_Rules sheet. `divisions` is serialized as
  // a semicolon-separated string to keep the row flat; parsed back on read.
  private dataAccessSheetName(): string {
    return this.config.sheets?.dataAccessRules || "Data_Access_Rules";
  }

  async listDataAccessRules(): Promise<any[]> {
    const sheetName = this.dataAccessSheetName();
    const headers = ["id", "hub", "divisions", "created_by", "created_at"];
    try {
      await this.ensureHeaders(sheetName, headers);
      const rows = await this.readSheet(sheetName);
      return rows
        .filter((row: any) => row.id || row.ID)
        .map((row: any) => ({
          id: String(row.id || row.ID || ""),
          hub: String(row.hub || row.HUB || row.Hub || ""),
          divisions: String(row.divisions || row.Divisions || "")
            .split(";")
            .map((s: string) => s.trim())
            .filter(Boolean),
          createdBy: String(row.created_by || row.createdBy || ""),
          createdAt: String(row.created_at || row.createdAt || ""),
        }));
    } catch (err: any) {
      console.warn("[Sheets] listDataAccessRules failed:", err.message);
      return [];
    }
  }

  async upsertDataAccessRule(payload: any): Promise<any> {
    const sheetName = this.dataAccessSheetName();
    const headers = ["id", "hub", "divisions", "created_by", "created_at"];
    await this.ensureHeaders(sheetName, headers);

    const id = String(payload.id || `dar-${Date.now()}`);
    const divisionsStr = Array.isArray(payload.divisions)
      ? payload.divisions.join(";")
      : String(payload.divisions || "");

    const rowValues = [
      id,
      String(payload.hub || ""),
      divisionsStr,
      String(payload.createdBy || ""),
      String(payload.createdAt || new Date().toISOString()),
    ];

    const existingIndex = await this.findRowIndex(sheetName, "id", id);
    if (existingIndex !== -1) {
      const sheetRow = existingIndex + 2;
      await this.request("update", {
        sheetName,
        range: `A${sheetRow}:E${sheetRow}`,
        values: [rowValues],
      });
    } else {
      await this.request("append", { sheetName, values: [rowValues] });
    }

    return {
      id,
      hub: String(payload.hub || ""),
      divisions: divisionsStr.split(";").filter(Boolean),
      createdBy: rowValues[3],
      createdAt: rowValues[4],
    };
  }

  async deleteDataAccessRule(id: string): Promise<{ id: string; deleted: boolean }> {
    const sheetName = this.dataAccessSheetName();
    const rowIndex = await this.findRowIndex(sheetName, "id", id);
    if (rowIndex === -1) return { id, deleted: false };
    const sheetRow = rowIndex + 2;
    await this.request("clear", {
      sheetName,
      range: `A${sheetRow}:E${sheetRow}`,
    });
    return { id, deleted: true };
  }

  async listAuditLog(): Promise<any[]> {
    const rows = await this.safeReadSheet(this.config.sheets.auditLog || "Audit_Log");
    if (!rows.length) return [];
    const headers = rows[0];
    return rows.slice(1).map((row: any) => this.zipRow(headers, row)).reverse(); // newest first
  }

  async appendAuditEntry(entry: any): Promise<void> {
    const sheet = this.config.sheets.auditLog || "Audit_Log";
    await this.request("append", {
      sheet,
      values: [[
        entry.id || `AUD-${Date.now()}`,
        entry.timestamp || new Date().toISOString(),
        entry.user || "system",
        entry.action || "",
        entry.targetType || "",
        entry.targetId || "",
        entry.details || "",
      ]],
    });
  }

  async listComments(targetType: string, targetId: string): Promise<any[]> {
    const rows = await this.safeReadSheet(this.config.sheets.comments || "Comments");
    if (!rows.length) return [];
    const headers = rows[0];
    return rows.slice(1)
      .map((row: any) => this.zipRow(headers, row))
      .filter((c: any) => c.targetType === targetType && c.targetId === targetId);
  }

  async appendComment(comment: any): Promise<any> {
    const sheet = this.config.sheets.comments || "Comments";
    const entry = {
      id: comment.id || `CMT-${Date.now()}`,
      targetType: comment.targetType || "",
      targetId: comment.targetId || "",
      author: comment.author || "user",
      text: comment.text || "",
      parentId: comment.parentId || "",
      createdAt: comment.createdAt || new Date().toISOString(),
    };
    await this.request("append", {
      sheet,
      values: [[entry.id, entry.targetType, entry.targetId, entry.author, entry.text, entry.parentId, entry.createdAt]],
    });
    return entry;
  }

  async listNotifications(): Promise<any[]> {
    const rows = await this.safeReadSheet(this.config.sheets.notifications || "Notifications");
    if (!rows.length) return [];
    const headers = rows[0];
    return rows.slice(1).map((row: any) => {
      const n = this.zipRow(headers, row);
      n.read = String(n.read).toLowerCase() === "true";
      return n;
    }).reverse();
  }

  async markNotificationRead(id: string): Promise<void> {
    // Best-effort — mark in local state only (no sheet update API for single cell)
  }

  async checkDuplicate(payload: { assetId: string; date: string }): Promise<boolean> {
    const forecasts = await this.listForecasts();
    return forecasts.some((row: any) => row.assetId === payload.assetId && row.date === payload.date);
  }

  async upsertForecast(payload: any): Promise<any> {
    const defaultHeaders = [
      "id", "assetId", "division", "brand", "country",
      "date", "quantity", "owner", "notes", "createdAt"
    ];

    await this.ensureHeaders(this.sheetName("forecasts"), defaultHeaders);

    const headerResult = await this.request("read", {
      sheetName: this.sheetName("forecasts"),
      range: `${this.sheetName("forecasts")}!1:1`
    });
    const headerValues = headerResult?.data?.values || headerResult?.values || [];
    const headers = headerValues[0] || defaultHeaders;

    const row: any = {
      id: payload.id || crypto.randomUUID(),
      assetId: payload.assetId || "",
      division: payload.division || "",
      brand: payload.brand || "",
      country: payload.country || "",
      date: payload.date || "",
      quantity: Number(payload.quantity || 0),
      owner: payload.owner || "",
      notes: payload.notes || "",
      createdAt: new Date().toISOString()
    };

    const fieldMap: Record<string, any> = {
      "id": row.id,
      "assetId": row.assetId,
      "Division": row.division,
      "division": row.division,
      "Brand": row.brand,
      "brand": row.brand,
      "Type": payload.type || "",
      "type": payload.type || "",
      "Project Name": payload.projectName || "",
      "Product Name": payload.productName || "",
      "ParentChild": payload.parentChild || "Child",
      "OCD": payload.ocd || "",
      "Launch Type": payload.launchType || "",
      "EAN": payload.ean || payload.globalEAN || payload.assetId || "",
      "HUB": row.country,
      "Confirmation": payload.confirmation || "Yes",
      "Launch date": row.date,
      "date": row.date,
      "Asset_Forecast": row.quantity,
      "quantity": row.quantity,
      "owner": row.owner,
      "notes": row.notes,
      "createdAt": row.createdAt
    };

    await this.request("append", {
      sheetName: this.sheetName("forecasts"),
      values: [headers.map((header: string) => fieldMap[header] ?? fieldMap[this.normalizeHeader(header)] ?? "")]
    });

    return row;
  }

  async syncValidatedToForecasts(): Promise<{ scannedAssets: number; appendedForecasts: number; existingForecasts: number }> {
    const defaultForecastHeaders = [
      "id", "assetId", "division", "brand", "country",
      "date", "quantity", "owner", "notes", "createdAt"
    ];

    await this.ensureHeaders(this.sheetName("forecasts"), defaultForecastHeaders);

    const forecastSheet = this.sheetName("forecasts");
    const headerResult = await this.request("read", {
      sheetName: forecastSheet,
      range: `${forecastSheet}!1:1`
    });
    const headerValues = headerResult?.data?.values || headerResult?.values || [];
    const forecastHeaders = headerValues[0] || defaultForecastHeaders;

    const assets = await this.listAssets();
    const forecasts = await this.listForecasts();
    const rules = await this.listRules();
    const rulesByHubDivision = new Map(
      rules.map((rule: any) => [
        `${this.normalizeHeader(rule.hub)}::${this.normalizeHeader(rule.division)}`,
        rule
      ])
    );
    const existing = new Set(forecasts.map((f: any) => `${f.assetId}::${f.date}`));

    const rowsToAppend: any[][] = [];

    for (const asset of assets) {
      const launchConfirmation = String((asset as any).launchConfirmation || (asset as any).status || "").trim().toLowerCase();
      const isValidated = ["yes", "validated", "ok", "true", "1"].includes(launchConfirmation);
      const isChild = String(asset.parentChild || "").trim().toLowerCase() === "child";
      const date = String(asset.requestedDeliveryDate || (asset as any).dmiMadDate || "").trim();
      const country = String(asset.hub || "").trim();

      if (!isValidated || !isChild || !date || !country) continue;

      const dedupeKey = `${asset.id}::${date}`;
      if (existing.has(dedupeKey)) continue;

      const ruleKey = `${this.normalizeHeader(asset.hub)}::${this.normalizeHeader(asset.division)}`;
      const rule: any = rulesByHubDivision.get(ruleKey);
      const nbAsset = rule
        ? (isChild ? Number(rule.nbOfAssetChild || 0) : Number(rule.nbOfAssetParent || 0))
        : (isChild ? 5 : 20);
      const nbLanguages = rule ? Number(rule.nbOfLanguages || 1) : 1;
      const translationPct = rule ? Number(rule.pctAssetTranslated || 0.8) : 0.8;
      const quantity = Math.round(nbAsset * nbLanguages * translationPct);

      const row: Record<string, any> = {
        id: crypto.randomUUID(),
        assetId: asset.id,
        division: asset.division || "",
        brand: asset.brand || "",
        type: asset.type || "",
        projectName: asset.projectName || "",
        productName: asset.productName || asset.name || "",
        parentChild: asset.parentChild || "Child",
        ocd: (asset as any).dmiMadDate || "",
        launchType: (asset as any).initiativeOrigin || "",
        ean: asset.globalEAN || "",
        country,
        date,
        quantity,
        owner: country,
        confirmation: "Yes",
        notes: "Auto-synced from validated Assets",
        createdAt: new Date().toISOString()
      };

      const fieldMap: Record<string, any> = {
        "id": row.id, "assetId": row.assetId,
        "Division": row.division, "division": row.division,
        "Brand": row.brand, "brand": row.brand,
        "Type": row.type, "type": row.type,
        "Project Name": row.projectName, "Product Name": row.productName,
        "ParentChild": row.parentChild, "OCD": row.ocd,
        "Launch Type": row.launchType, "EAN": row.ean,
        "HUB": row.country, "Confirmation": row.confirmation,
        "Launch date": row.date, "date": row.date,
        "Asset_Forecast": row.quantity, "quantity": row.quantity,
        "owner": row.owner, "notes": row.notes,
        "createdAt": row.createdAt
      };

      rowsToAppend.push(
        forecastHeaders.map((h: string) => fieldMap[h] ?? fieldMap[this.normalizeHeader(h)] ?? "")
      );
      existing.add(dedupeKey);
    }

    const chunkSize = 500;
    for (let i = 0; i < rowsToAppend.length; i += chunkSize) {
      const chunk = rowsToAppend.slice(i, i + chunkSize);
      await this.request("append", {
        sheetName: this.sheetName("forecasts"),
        values: chunk
      });
    }

    return {
      scannedAssets: assets.length,
      appendedForecasts: rowsToAppend.length,
      existingForecasts: forecasts.length
    };
  }

  // ── Private helpers ──────────────────────────────────────────

  private async request(action: string, payload: any = {}): Promise<any> {
    return this.throttledRequest(action, payload);
  }

  private async makeRequest(action: string, payload: any = {}): Promise<any> {
    if (!this.config.endpoint) {
      throw new Error("Google Sheets endpoint is not configured.");
    }

    const isProxy = this.config.endpoint.startsWith("/");

    if (isProxy) {
      const body = this.config.endpoint === "/api/sheets"
        ? { action, ...payload }
        : { action, payload, apiKey: (this.config as any).apiKey || "" };

      const response = await fetch(this.config.endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        let errBody = "";
        try { errBody = await response.text(); } catch { /* ignore */ }
        console.error(`[Sheets] ${action} failed ${response.status}:`, errBody);
        throw new Error(`Google provider failed: ${response.status} — ${errBody}`);
      }

      const data = await response.json();
      return data.result;
    }

    const form = new URLSearchParams({
      action,
      payload: JSON.stringify(payload),
      apiKey: (this.config as any).apiKey || ""
    });

    const response = await fetch(this.config.endpoint, {
      method: "POST",
      body: form
    });

    if (!response.ok) {
      throw new Error(`Google provider failed: ${response.status}`);
    }

    const data = await response.json();
    return data.result;
  }

  private sheetName(key: string): string {
    return this.config.sheets?.[key] || key;
  }

  private async readSheet(sheetName: string): Promise<any[]> {
    const result = await this.request("read", {
      sheetName,
      range: `${sheetName}!A:ZZ`
    });

    const values = result?.data?.values || result?.values || [];
    if (!Array.isArray(values) || values.length <= 1) return [];

    const [headerRow, ...dataRows] = values;
    const headers = headerRow.map((header: string) => this.normalizeHeader(header));

    return dataRows
      .filter((row: any[]) => Array.isArray(row) && row.some((cell: any) => String(cell || "").trim() !== ""))
      .map((row: any[]) => this.zipRow(headers, row));
  }

  private async ensureHeaders(sheetName: string, headers: string[]): Promise<void> {
    const result = await this.request("read", {
      sheetName,
      range: `${sheetName}!1:1`
    });

    const values = result?.data?.values || result?.values || [];
    if (!Array.isArray(values) || values.length === 0 || values[0].length === 0) {
      await this.request("append", { sheetName, values: [headers] });
    }
  }

  private zipRow(headers: string[], row: any[]): Record<string, any> {
    return headers.reduce((acc: Record<string, any>, header: string, index: number) => {
      acc[header] = row[index] ?? "";
      return acc;
    }, {});
  }

  private normalizeHeader(value: any): string {
    return String(value || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  }

  private pick(row: any, candidates: string[], fallback: any = ""): any {
    for (const candidate of candidates) {
      const key = this.normalizeHeader(candidate);
      if (row[key] !== undefined && row[key] !== null && String(row[key]).trim() !== "") {
        return row[key];
      }
    }
    return fallback;
  }

  private mapAssetRow(row: any, index: number): any {
    const productName = this.pick(row, ["product_name", "productname", "product"]);
    const projectName = this.pick(row, ["project_name", "projectname"]);
    const id = this.pick(row, ["id"], "") || this.buildAssetId(row, index);

    return {
      id,
      name: productName || projectName || `Asset ${index + 1}`,
      quarter: this.normalizeQuarter(this.pick(row, ["quarter", "qtr", "quarter_year"])),
      division: this.pick(row, ["division"]),
      brand: this.pick(row, ["brand"]),
      type: this.pick(row, ["type"]),
      projectName,
      productName,
      parentChild: this.pick(row, ["parent_child", "parentchild", "parent_child_label"]),
      dmiMadDate: this.normalizeDate(this.pick(row, ["dmi_mad_date"])),
      initiativeOrigin: this.pick(row, ["initiative_origine_zone_local", "initiative_origin_zone_local", "initiative_origine", "initiative_origin"]),
      globalEAN: this.pick(row, ["global_ean", "ean"]),
      hub: this.pick(row, ["hub"]),
      launchConfirmation: this.pick(row, ["launch_confirmation"]),
      requestedDeliveryDate: this.normalizeDate(this.pick(row, ["requested_delivery_date"])),
      status: this.pick(row, ["launch_confirmation"], ""),
      assetVolume: this.pick(row, ["asset_volume", "calculated_asset_volume", "assetforecast", "asset_forecast"], ""),
      nbAsset: this.pick(row, ["nb_of_asset", "nb_asset", "number_of_assets", "asset_count"], ""),
      nbLanguages: this.pick(row, ["nb_of_languages", "nb_languages", "nb_language", "nb_of_langu"], ""),
      pctAssetTranslated: this.pick(row, ["pct_asset_translated", "%_asset_translated", "asset_translation", "translation_pct"], "")
    };
  }

  private mapForecastRow(row: any, index: number): any {
    const assetId = this.pick(row, ["asset_id", "assetid", "ean", "global_ean", "id"]);
    const quantity = Number(this.pick(row, ["quantity", "qty", "forecast_qty", "asset_forecast"], 0)) || 0;

    if (!assetId && !quantity) return null;

    return {
      id: this.pick(row, ["id"], `forecast-${index + 1}`),
      assetId,
      division: this.pick(row, ["division"]),
      brand: this.pick(row, ["brand"]),
      country: this.pick(row, ["country", "hub"]),
      date: this.normalizeDate(this.pick(row, ["date", "launch_date", "forecast_date", "requested_delivery_date"])),
      quantity,
      owner: this.pick(row, ["owner", "user", "email"]),
      notes: this.pick(row, ["notes", "comment", "comments"]),
      createdAt: this.normalizeDateTime(this.pick(row, ["created_at", "createdat"])),
      // EF-DATA-1: filled by listForecasts() join when missing on the row
      projectName: this.pick(row, ["project_name", "projectname"], ""),
      type: this.pick(row, ["type", "launch_type"], ""),
      ocdDate: this.normalizeDate(this.pick(row, ["ocd_date", "ocd", "dmi_mad_date", "brief_date"], "")),
    };
  }

  private mapRuleRow(row: any, index: number): any {
    const hub = this.pick(row, ["hub"]);
    const division = this.pick(row, ["division"]);

    if (!hub && !division) return null;

    return {
      id: this.pick(row, ["id"], `rule-${index + 1}`),
      hub,
      division,
      nbOfAssetParent: Number(this.pick(row, ["nb_of_asset_parent", "nb_asset_parent", "parent_nb_of_asset"], 20)) || 20,
      nbOfAssetChild: Number(this.pick(row, ["nb_of_asset_child", "nb_asset_child", "child_nb_of_asset"], 5)) || 5,
      nbOfLanguages: Number(this.pick(row, ["nb_of_languages", "nb_languages", "nb_language", "nb_of_langu"], 1)) || 1,
      // EF-DATA-2: sheet stores languages separated by TABs (sometimes ; or ,) — normalize
      languageList: String(this.pick(row, ["language_list", "languages"], ""))
        .split(/[\t;,\n]+/)
        .map((s) => s.trim())
        .filter(Boolean)
        .join(", "),
      pctAssetTranslated: this.toPercent(this.pick(row, ["%_asset_translated", "%_asset_translation", "asset_translated", "asset_translation", "pct_asset_translation"], 0.8))
    };
  }

  private async findRowIndex(sheetName: string, column: string, value: string): Promise<number> {
    const result = await this.request("read", {
      sheetName,
      range: `${sheetName}!A:ZZ`
    });

    const values = result?.data?.values || result?.values || [];
    if (!Array.isArray(values) || values.length <= 1) return -1;

    const [headerRow, ...dataRows] = values;
    const headers = headerRow.map((h: string) => this.normalizeHeader(h));
    const colIndex = headers.indexOf(this.normalizeHeader(column));
    if (colIndex === -1) return -1;

    return dataRows.findIndex((row: any[]) => row[colIndex] === value);
  }

  private async resolveVolumeInputs(payload: any): Promise<{ nbAsset: number; nbLanguages: number; translationPct: number }> {
    const hub = String(payload.hub || "").trim();
    const division = String(payload.division || "").trim();
    const type = String(payload.type || "").trim();
    const parentChild = String(payload.parentChild || payload.parent_child || "Parent").trim();

    const consolidatedRules = await this.listRules();
    const ruleMatch = consolidatedRules.find((rule: any) =>
      this.same(rule.hub, hub) && this.same(rule.division, division)
    );

    if (ruleMatch) {
      return {
        nbAsset: parentChild.toLowerCase() === "child" ? ruleMatch.nbOfAssetChild : ruleMatch.nbOfAssetParent,
        nbLanguages: ruleMatch.nbOfLanguages,
        translationPct: ruleMatch.pctAssetTranslated
      };
    }

    let nbLanguages = hub.toUpperCase() === "DACH" ? 3 : 1;
    let translationPct = 0.8;
    let nbAsset = parentChild.toLowerCase() === "child" ? 5 : 20;

    const languageRows = await this.safeReadSheet(this.sheetName("configLanguage"));
    const translationRows = await this.safeReadSheet(this.sheetName("configTranslation"));
    const assetRows = await this.safeReadSheet(this.sheetName("configAsset"));

    const languageMatch = languageRows.find((row: any) =>
      this.same(this.pick(row, ["hub"]), hub)
    );
    if (languageMatch) {
      const parsed = this.toNumber(this.pick(languageMatch, ["nb_of_langu", "nb_of_languages", "nb_languages", "nb_language", "languages"]));
      if (parsed !== null) nbLanguages = parsed;
    }

    const translationMatch = translationRows.find((row: any) =>
      this.same(this.pick(row, ["hub"]), hub) && this.same(this.pick(row, ["division"]), division)
    );
    if (translationMatch) {
      const parsed = this.toNumber(this.pick(translationMatch, ["_asset_translation", "asset_translation", "asset_translated", "pct_asset_translation", "%_asset_translation", "%_asset_translated", "translation_pct"]));
      if (parsed !== null) translationPct = parsed > 1 ? parsed / 100 : parsed;
    }

    const assetMatch = assetRows.find((row: any) =>
      this.same(this.pick(row, ["hub"]), hub) &&
      this.same(this.pick(row, ["division"]), division) &&
      this.same(this.pick(row, ["type"]), type)
    );
    if (assetMatch) {
      const parsed = this.toNumber(this.pick(assetMatch, ["nb_of_asset", "nb_asset", "number_of_assets", "asset_count"]));
      if (parsed !== null) nbAsset = parsed;
    }

    return { nbAsset, nbLanguages, translationPct };
  }

  private async safeReadSheet(sheetName: string): Promise<any[]> {
    if (!sheetName) return [];
    try {
      return await this.readSheet(sheetName);
    } catch {
      return [];
    }
  }

  private same(a: any, b: any): boolean {
    return this.normalizeHeader(a) === this.normalizeHeader(b);
  }

  private toNumber(value: any): number | null {
    if (value === undefined || value === null) return null;
    const cleaned = String(value)
      .trim()
      .replace(/%/g, "")
      .replace(/\s+/g, "")
      .replace(/,/g, ".");
    if (!cleaned) return null;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private toPercent(value: any): number {
    const parsed = this.toNumber(value);
    if (parsed === null) return 0.8;
    return parsed > 1 ? parsed / 100 : parsed;
  }

  private buildAssetId(row: any, index: number): string {
    const brand = this.slug(this.pick(row, ["brand"], "asset"));
    const product = this.slug(this.pick(row, ["product_name", "productname"], `row-${index + 1}`));
    const ean = this.slug(this.pick(row, ["global_ean", "ean"], String(index + 1)));
    const hub = this.slug(this.pick(row, ["hub"], "master"));
    return `${brand}-${product}-${ean}-${hub}`;
  }

  private slug(value: any): string {
    return String(value || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40) || "na";
  }

  private normalizeQuarter(value: any): string {
    const raw = String(value || "").trim();
    const match = raw.match(/^(\d)Q\s*(\d{4})$/i);
    if (match) return `Q${match[1]} ${match[2]}`;
    return raw;
  }

  private normalizeDate(value: any): string {
    const raw = String(value || "").trim();
    if (!raw) return "";

    const ddmmyyyy = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (ddmmyyyy) {
      const [, day, month, year] = ddmmyyyy;
      return `${year}-${month}-${day}`;
    }

    return raw;
  }

  private normalizeDateTime(value: any): string {
    return String(value || "").trim();
  }
}
